/**
 * DoctorVerifyList (iter100.262) — the pending-verification queue as a
 * reusable list. Rendered inline in the Providers command center when
 * the "Pending verification" tile is active, so admins never have to
 * leave the roster to approve credentials.
 *
 * Consumes the same /admin/doctors/lifecycle/pending endpoint so counts
 * always match what the Providers roster shows.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    ShieldCheck, XCircle, CheckCircle, User, ClockCounterClockwise,
    Buildings, IdentificationCard, ArrowSquareOut,
} from "@phosphor-icons/react";

const fmtDateTime = (s) =>
    s ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";


export default function DoctorVerifyList({ onQueueChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [rejectFor, setRejectFor] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/doctors/lifecycle/pending?limit=50")
            .then((r) => {
                const list = r.data?.items || [];
                setItems(list);
                onQueueChange?.(list.length);
            })
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const verify = async (row) => {
        setBusy(row.id);
        try {
            await api.post(`/admin/doctors/${row.id}/lifecycle/verify`, {
                reason: "Credentials reviewed and approved",
            });
            toast.success(`${row.name} verified. Doctor can now click "Go live" from their dashboard.`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verify failed");
        } finally {
            setBusy(null);
        }
    };

    const publish = async (row) => {
        setBusy(row.id);
        try {
            await api.post(`/admin/doctors/${row.id}/lifecycle/verify`, {
                reason: "Credentials reviewed and approved",
            });
            await api.post(`/admin/doctors/${row.id}/lifecycle/publish`, {
                reason: "Verified and published by admin",
            });
            toast.success(`${row.name} verified + published. Now visible in Find-a-Doctor.`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Publish failed");
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return <div className="text-sm text-muted-foreground py-6 text-center">Loading pending queue…</div>;
    }
    if (items.length === 0) {
        return (
            <div className="text-center py-12 rounded-2xl border border-dashed border-border bg-card" data-testid="verification-empty">
                <ShieldCheck size={40} weight="duotone" className="text-primary/40 mx-auto mb-3" />
                <div className="font-display text-lg font-semibold mb-1">Queue is empty</div>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    No doctors currently awaiting verification. New doctors show up here automatically as soon as they cross 80% profile completeness.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-2" data-testid="verification-queue-list">
            {items.map((row) => (
                <PendingRow
                    key={row.id}
                    row={row}
                    busy={busy === row.id}
                    onVerify={() => verify(row)}
                    onPublish={() => publish(row)}
                    onReject={() => setRejectFor(row)}
                />
            ))}
            <RejectDialog
                doctor={rejectFor}
                onClose={() => setRejectFor(null)}
                onDone={() => { setRejectFor(null); load(); }}
            />
        </div>
    );
}


function PendingRow({ row, busy, onVerify, onPublish, onReject }) {
    return (
        <div
            className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-center p-4 bg-card border border-border rounded-xl hover:border-primary/30 transition"
            data-testid={`verification-row-${row.id}`}
        >
            <div className="min-w-0 flex items-start gap-3">
                <div className="mt-0.5">
                    <User size={22} weight="duotone" className="text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{row.name}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                            PENDING · {row.completeness_score}%
                        </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                        {row.specialization && <span>{row.specialization}</span>}
                        {row.city && (
                            <span className="flex items-center gap-1">
                                <Buildings size={12} weight="duotone" />
                                {row.city}
                            </span>
                        )}
                        {row.registration_number && (
                            <span className="flex items-center gap-1 font-mono text-[10px]">
                                <IdentificationCard size={12} weight="duotone" />
                                {row.registration_number}
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <ClockCounterClockwise size={12} weight="duotone" />
                            in queue since {fmtDateTime(row.lifecycle_updated_at)}
                        </span>
                    </div>
                </div>
            </div>

            <Link
                to={`/admin/doctors/${row.id}`}
                className="text-xs text-primary hover:underline flex items-center gap-1"
                data-testid={`verification-open-${row.id}`}
            >
                <ArrowSquareOut size={12} weight="bold" /> Full profile
            </Link>

            <div className="flex items-center gap-1.5">
                <Button
                    variant="outline"
                    size="sm"
                    className="text-rose-700 hover:bg-rose-50"
                    onClick={onReject}
                    disabled={busy}
                    data-testid={`verification-reject-${row.id}`}
                >
                    <XCircle size={13} weight="bold" className="mr-1" /> Reject
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onVerify}
                    disabled={busy}
                    data-testid={`verification-verify-${row.id}`}
                >
                    <CheckCircle size={13} weight="bold" className="mr-1" /> Verify only
                </Button>
                <Button
                    size="sm"
                    onClick={onPublish}
                    disabled={busy}
                    data-testid={`verification-publish-${row.id}`}
                >
                    <ShieldCheck size={13} weight="fill" className="mr-1" /> {busy ? "Working…" : "Verify + Publish"}
                </Button>
            </div>
        </div>
    );
}


function RejectDialog({ doctor, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => { if (doctor) setReason(""); }, [doctor]);

    const submit = async () => {
        if (!reason.trim()) {
            toast.error("Reason is required so the doctor knows what to fix");
            return;
        }
        setSending(true);
        try {
            await api.post(`/admin/doctors/${doctor.id}/lifecycle/reject`, {
                reason: reason.trim(),
            });
            toast.success(`${doctor.name} moved back to Draft. They'll see the reason on their onboarding screen.`);
            onDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reject failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={!!doctor} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="verification-reject-dialog">
                <DialogHeader>
                    <DialogTitle>Reject {doctor?.name}?</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <p className="text-sm text-muted-foreground">
                        The doctor will return to <b>Draft</b> status. They&apos;ll see your reason on their onboarding dashboard and can re-submit after fixing it.
                    </p>
                    <div>
                        <Label className="text-xs font-semibold">Reason for rejection (visible to doctor)</Label>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g., Registration number could not be verified against the state council registry. Please re-upload a legible certificate."
                            className="mt-1.5"
                            rows={4}
                            data-testid="verification-reject-reason"
                        />
                    </div>
                </div>
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                    <Button
                        variant="destructive"
                        onClick={submit}
                        disabled={sending}
                        data-testid="verification-reject-confirm"
                    >
                        {sending ? "Rejecting…" : "Reject and send back"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
